import { useEffect } from "react";

import { PalButton, PalSwitch, PalTextField, PalTimePicker, PalTypography } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Cancel, SupportAgent } from "@mui/icons-material";
import { Box, Chip, Grid } from "@mui/material";

import PRModal from "~components/Generic/PRModal";
import { selectProject } from "~store/settings/projectSettings/selectors";
import { getCallcenterSettings } from "~store/socket/livechat/actions";

import { HelpIconWrapper, StepContainer } from "../CommonComponents";

const AgentAddModal = withCardon(
  function TemplateUploadModalContent({ get }) {
    const formik = useFormik({
      initialValues: {
        email: "",
      },
      validationSchema: Yup.object({
        email: Yup.string().email("Invalid email address").required("Required"),
      }),
      onSubmit: async (values) => {
        get(values.email)();
      },
    });

    const handleChange = (e) => {
      formik.setFieldValue("email", e.target.value);
    };
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        formik.handleSubmit();
      }
    };
    return (
      <PRModal title={"Invite Agent"} onClick={formik.handleSubmit} onClose={get(false)}>
        <PalTextField
          fullWidth
          invalid={formik.touched.email && formik.errors.email}
          label="Enter Email"
          value={formik.values.email}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
        />
      </PRModal>
    );
  },
  { destroyOnHide: true }
);

function StepLivechat({ formik, stepKey, setFieldValue }) {
  const dispatch = useDispatch();
  const project = useSelector(selectProject);
  // const callcenterSettings = useSelector(selectCallcenterSettings);
  const handleAddAgent = async () => {
    const email = await AgentAddModal.show();
    if (email) {
      setFieldValue("agentList", [...formik.values[stepKey].agentList, email]);
    }
  };

  const handleChipDelete = (index) => () => {
    setFieldValue(
      "agentList",
      formik.values[stepKey].agentList.filter((_, i) => i !== index)
    );
  };

  const handleChangeTimePicket = (key) => (date) => {
    setFieldValue(key, date);
  };

  useEffect(() => {
    if (!project?.id) return;
    dispatch(getCallcenterSettings(project.id));
  }, [dispatch, project?.id]);

  const handleChangeAgentCanLeaveSession = (key) => (e) => {
    setFieldValue(key, !e.target.checked);
  };
  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            Livechat
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please specify whether you would like to enable live chat for your assistant. This will allow users to
            interact with your assistant in real-time.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            <HelpIconWrapper title="Default behavior is to keep the agent in the session until the user ends the chat. If enabled, the agent can leave the session and the user will be transferred to the bot.">
              Disable Agent-to-Bot Transfer
            </HelpIconWrapper>
          </PalTypography>
          <PalSwitch
            checked={!formik.values[stepKey].agent_can_leave_session}
            onChange={handleChangeAgentCanLeaveSession("agent_can_leave_session")}
          />
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Work Hours
          </PalTypography>
          <Box display="flex" flexDirection="row" gap={2}>
            <PalTimePicker
              fullWidth
              label="Select Start Time"
              value={formik.values[stepKey].work_start_time}
              onChange={handleChangeTimePicket("work_start_time")}
            />
            <PalTimePicker
              fullWidth
              label="Select End Time"
              value={formik.values[stepKey].work_end_time}
              onChange={handleChangeTimePicket("work_end_time")}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Invite Agents for Livechat
          </PalTypography>
          {!formik.values[stepKey].agentList?.length && (
            <Box
              mt={2}
              p={2}
              sx={{
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                textAlign: "center",
              }}
            >
              <PalTypography color="textSecondary">No agents invited yet.</PalTypography>
            </Box>
          )}
          {formik.values[stepKey].agentList?.length > 0 && (
            <Box
              display={"flex"}
              flexWrap={"wrap"}
              gap={1}
              mt={2}
              p={2}
              sx={{
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            >
              {formik.values[stepKey].agentList.map((email, index) => (
                <Box
                  key={index}
                  alignItems={"center"}
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <Box>
                    <Chip
                      deleteIcon={<Cancel />}
                      icon={<SupportAgent />}
                      label={email}
                      onDelete={handleChipDelete(index)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box display={"flex"} flexDirection={"row"} justifyContent={"flex-end"} mt={1}>
            <PalButton color="primary" size="medium" variant="text" onClick={handleAddAgent}>
              Invite
            </PalButton>
          </Box>
        </Grid>
      </Grid>
    </StepContainer>
  );
}

export default StepLivechat;
